import React from 'react';

/**
 * Parses inline markdown + color tags into React elements.
 * Supports: **bold**, *italic*, [red]...[/red], [yellow]...[/yellow], [green]...[/green], `code`
 */
function parseInline(text, keyPrefix = '') {
  const parts = [];
  let remaining = text;
  let idx = 0;

  const COLOR_MAP = {
    red: 'text-red-400 font-medium',
    yellow: 'text-yellow-400 font-medium',
    green: 'text-green-400 font-medium',
  };

  while (remaining.length > 0) {
    // Find the earliest match among all inline patterns
    let earliest = null;
    let earliestIndex = remaining.length;

    // Color tags: [red]...[/red], [yellow]...[/yellow], [green]...[/green]
    for (const color of Object.keys(COLOR_MAP)) {
      const openTag = `[${color}]`;
      const closeTag = `[/${color}]`;
      const oi = remaining.indexOf(openTag);
      if (oi !== -1 && oi < earliestIndex) {
        const ci = remaining.indexOf(closeTag, oi + openTag.length);
        if (ci !== -1) {
          earliest = { type: 'color', color, start: oi, openLen: openTag.length, end: ci, closeLen: closeTag.length };
          earliestIndex = oi;
        }
      }
    }

    // Bold: **...**
    const boldIdx = remaining.indexOf('**');
    if (boldIdx !== -1 && boldIdx < earliestIndex) {
      const closeIdx = remaining.indexOf('**', boldIdx + 2);
      if (closeIdx !== -1) {
        earliest = { type: 'bold', start: boldIdx, openLen: 2, end: closeIdx, closeLen: 2 };
        earliestIndex = boldIdx;
      }
    }

    // Italic: *...* (but not **)
    if (!earliest || earliest.type !== 'bold') {
      const italicRegex = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/;
      const italicMatch = remaining.match(italicRegex);
      if (italicMatch && italicMatch.index !== undefined && italicMatch.index < earliestIndex) {
        earliest = { type: 'italic', start: italicMatch.index, openLen: 1, end: italicMatch.index + 1 + italicMatch[1].length, closeLen: 1 };
        earliestIndex = italicMatch.index;
      }
    }

    // Inline code: `...`
    const codeIdx = remaining.indexOf('`');
    if (codeIdx !== -1 && codeIdx < earliestIndex) {
      const closeIdx = remaining.indexOf('`', codeIdx + 1);
      if (closeIdx !== -1) {
        earliest = { type: 'code', start: codeIdx, openLen: 1, end: closeIdx, closeLen: 1 };
        earliestIndex = codeIdx;
      }
    }

    if (!earliest) {
      // No more patterns — push the rest as text
      parts.push(remaining);
      break;
    }

    // Push text before the match
    if (earliest.start > 0) {
      parts.push(remaining.slice(0, earliest.start));
    }

    const inner = remaining.slice(earliest.start + earliest.openLen, earliest.end);
    const k = `${keyPrefix}-${idx++}`;

    if (earliest.type === 'bold') {
      parts.push(<strong key={k} className="font-semibold">{parseInline(inner, k)}</strong>);
    } else if (earliest.type === 'italic') {
      parts.push(<em key={k} className="italic opacity-90">{inner}</em>);
    } else if (earliest.type === 'code') {
      parts.push(<code key={k} className="bg-white/10 px-1.5 py-0.5 rounded text-[12px] font-mono">{inner}</code>);
    } else if (earliest.type === 'color') {
      parts.push(<span key={k} className={COLOR_MAP[earliest.color]}>{parseInline(inner, k)}</span>);
    }

    remaining = remaining.slice(earliest.end + earliest.closeLen);
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

/**
 * Renders a single line, deciding if it's a heading, list item, or paragraph.
 */
function renderLine(line, lineIndex) {
  const k = `line-${lineIndex}`;

  // Heading: ### ...
  if (line.startsWith('### ')) {
    return <h3 key={k} className="text-sm font-bold mt-3 mb-1 text-white">{parseInline(line.slice(4), k)}</h3>;
  }
  if (line.startsWith('## ')) {
    return <h2 key={k} className="text-sm font-bold mt-3 mb-1 text-white">{parseInline(line.slice(3), k)}</h2>;
  }

  // Numbered list: "1. ..." or "2. ..."
  const numberedMatch = line.match(/^(\d+)\.\s+(.+)/);
  if (numberedMatch) {
    return (
      <div key={k} className="flex gap-2 items-start ml-1 my-0.5">
        <span className="text-primary font-bold text-[12px] min-w-[16px] flex-shrink-0 mt-px">{numberedMatch[1]}.</span>
        <span className="flex-1">{parseInline(numberedMatch[2], k)}</span>
      </div>
    );
  }

  // Bullet list: "- ..." or "* ..."
  const bulletMatch = line.match(/^[-*]\s+(.+)/);
  if (bulletMatch) {
    return (
      <div key={k} className="flex gap-2 items-start ml-1 my-0.5">
        <span className="text-primary text-[8px] mt-[5px] flex-shrink-0">&#9679;</span>
        <span className="flex-1">{parseInline(bulletMatch[1], k)}</span>
      </div>
    );
  }

  // Horizontal rule
  if (line.match(/^---+$/)) {
    return <hr key={k} className="border-white/10 my-2" />;
  }

  // Empty line = paragraph break
  if (line.trim() === '') {
    return <div key={k} className="h-2" />;
  }

  // Regular text
  return <p key={k} className="my-0.5">{parseInline(line, k)}</p>;
}

/**
 * Parses ticket and preference actions out of content. Returns { cleanContent, actions[] }.
 */
export function parseTicketActions(content) {
  if (!content) return { cleanContent: content, actions: [] };

  const actions = [];
  let clean = content;

  const ticketRegex = /\[TICKET:(.*?)\]([\s\S]*?)\[\/TICKET\]/g;
  const updateRegex = /\[UPDATE_TICKET:(.*?)\]([\s\S]*?)\[\/UPDATE_TICKET\]/g;
  const closeRegex = /\[CLOSE_TICKET:(.*?)\]([\s\S]*?)\[\/CLOSE_TICKET\]/g;
  const prefRegex = /\[PREF_UPDATE:(.*?)\]([\s\S]*?)\[\/PREF_UPDATE\]/g;

  let match;
  while ((match = ticketRegex.exec(content)) !== null) {
    actions.push({ type: 'create', subject: match[1].trim(), description: match[2].trim() });
    clean = clean.replace(match[0], '');
  }
  while ((match = updateRegex.exec(content)) !== null) {
    actions.push({ type: 'update', ticketId: match[1].trim(), comment: match[2].trim() });
    clean = clean.replace(match[0], '');
  }
  while ((match = closeRegex.exec(content)) !== null) {
    actions.push({ type: 'close', ticketId: match[1].trim(), reason: match[2].trim() });
    clean = clean.replace(match[0], '');
  }
  while ((match = prefRegex.exec(content)) !== null) {
    actions.push({ type: 'pref_update', field: match[1].trim(), value: match[2].trim() });
    clean = clean.replace(match[0], '');
  }

  // Quick reply buttons: [BUTTONS]Label 1|Label 2|Label 3[/BUTTONS] or [BUTTONS]Label 1|Label 2 (no closing tag)
  const buttonsRegex = /\[BUTTONS\](.*?)(?:\[\/BUTTONS\]|$)/gs;
  while ((match = buttonsRegex.exec(content)) !== null) {
    const labels = match[1].split('|').map(s => s.trim()).filter(Boolean);
    if (labels.length > 0) {
      actions.push({ type: 'buttons', labels });
    }
    clean = clean.replace(match[0], '');
  }

  return { cleanContent: clean.trim(), actions };
}

const PREF_LABELS = {
  surf_level: 'Surf Level',
  home_break: 'Home Break',
  preferred_wave_types: 'Wave Types',
  preferred_wind: 'Preferred Wind',
  preferred_swell_direction: 'Preferred Swell',
  preferred_tide: 'Preferred Tide',
  preferred_session_time: 'Session Time',
  min_wave_height: 'Min Wave Height',
  max_wave_height: 'Max Wave Height',
  wetsuit_thickness: 'Wetsuit Thickness',
  favorite_spots: 'Favorite Spots',
};

/**
 * Renders a preference update action card.
 */
export function PrefUpdateCard({ action, onUpdatePref }) {
  const [applied, setApplied] = React.useState(false);
  const label = PREF_LABELS[action.field] || action.field;

  const handleApply = () => {
    if (onUpdatePref) {
      onUpdatePref(action.field, action.value);
      setApplied(true);
    }
  };

  return (
    <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-3" data-testid={`pref-update-card-${action.field}`}>
      <p className="text-[10px] uppercase tracking-widest text-secondary/70 mb-1">Update Preference</p>
      <div className="flex items-center justify-between mb-2.5">
        <div>
          <p className="text-[10px] text-muted-foreground/50">{label}</p>
          <p className="text-xs font-semibold text-white">{action.value}</p>
        </div>
      </div>
      {applied ? (
        <div className="w-full bg-green-500/15 text-green-400 text-xs font-bold py-2 rounded-lg text-center" data-testid={`pref-applied-${action.field}`}>
          Updated
        </div>
      ) : (
        <button
          data-testid={`apply-pref-${action.field}`}
          onClick={handleApply}
          className="w-full bg-secondary text-black text-xs font-bold py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          Apply Change
        </button>
      )}
    </div>
  );
}

/**
 * Renders a ticket action card (used as a standalone chat bubble).
 */
export function TicketActionCard({ action, onCreateTicket, onUpdateTicket, onCloseTicket }) {
  if (action.type === 'create') {
    return (
      <div className="bg-primary/10 border border-primary/20 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-widest text-primary/70 mb-1">New Support Ticket</p>
        <p className="text-xs font-semibold text-white mb-0.5">{action.subject}</p>
        <p className="text-[11px] text-muted-foreground mb-2.5 line-clamp-2">{action.description}</p>
        {onCreateTicket && (
          <button data-testid="create-ticket-btn" onClick={() => onCreateTicket(action.subject, action.description)}
            className="w-full bg-primary text-black text-xs font-bold py-2 rounded-lg hover:opacity-90 transition-opacity">
            Create Ticket
          </button>
        )}
      </div>
    );
  }
  if (action.type === 'update') {
    return (
      <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-widest text-yellow-400/70 mb-1">Update Ticket</p>
        <p className="text-xs font-semibold text-white mb-0.5">{action.ticketId}</p>
        <p className="text-[11px] text-muted-foreground mb-2.5 line-clamp-2">{action.comment}</p>
        {onUpdateTicket && (
          <button data-testid="update-ticket-btn" onClick={() => onUpdateTicket(action.ticketId, action.comment)}
            className="w-full bg-yellow-400 text-black text-xs font-bold py-2 rounded-lg hover:opacity-90 transition-opacity">
            Update Ticket
          </button>
        )}
      </div>
    );
  }
  if (action.type === 'close') {
    return (
      <div className="bg-muted-foreground/10 border border-muted-foreground/20 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mb-1">Close Ticket</p>
        <p className="text-xs font-semibold text-white mb-0.5">{action.ticketId}</p>
        <p className="text-[11px] text-muted-foreground mb-2.5 line-clamp-2">{action.reason}</p>
        {onCloseTicket && (
          <button data-testid="close-ticket-btn" onClick={() => onCloseTicket(action.ticketId, action.reason)}
            className="w-full bg-muted-foreground text-black text-xs font-bold py-2 rounded-lg hover:opacity-90 transition-opacity">
            Close Ticket
          </button>
        )}
      </div>
    );
  }
  return null;
}

/**
 * Main component: renders markdown-formatted chat message content.
 * Ticket actions are stripped out — use parseTicketActions + TicketActionCard separately.
 */
export default function MarkdownMessage({ content, className = '' }) {
  if (!content) return null;

  // Strip any ticket tags so they don't render inline
  const { cleanContent } = parseTicketActions(content);
  if (!cleanContent) return null;

  const lines = cleanContent.split('\n');

  return (
    <div className={`markdown-msg space-y-0 ${className}`}>
      {lines.map((line, i) => renderLine(line, i))}
    </div>
  );
}
